import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  SlidersHorizontal,
  TrendingUp,
  TrendingDown,
  ArrowLeftRight,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_adjustment_report,
  get_report_filter_data,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import XLSX from "sheetjs-style";
import { handleError } from "../CommonJquery/CommonJquery";

// Backend filters on adjustmentmanagementdetails__difference_type, whose
// values are 'Gain' / 'Loss' (title case) — not 'GAIN' / 'LOSS'.
const ADJUSTMENT_TYPES = [
  { value: "Gain", label: "Gain" },
  { value: "Loss", label: "Loss" },
];

const typeBadgeClass = (type) => {
  switch ((type || "").toUpperCase()) {
    case "GAIN":
      return "bg-success/10 text-success";
    case "LOSS":
      return "bg-destructive/10 text-destructive";
    default:
      return "bg-muted text-muted-foreground";
  }
};

export default function AdjustmentReport() {
  const { toast } = useToast();

  // Filter states
  const [filterOption, setFilterOption] = useState("");
  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());
  const [differenceType, setDifferenceType] = useState("all");
  const [reason, setReason] = useState("");
  const [departmentId, setDepartmentId] = useState("all");

  // Filter dropdown data
  const [departments, setDepartments] = useState([]);

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState([]);
  const [expandedAdjId, setExpandedAdjId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Stats (from API summary) — the backend only tracks quantities, it does
  // not compute any monetary gain/loss value.
  const [stats, setStats] = useState({
    totalAdjustments: 0,
    totalGainQty: 0,
    totalLossQty: 0,
    netAdjustment: 0,
  });

  // Format number
  const formatNumber = (num) => {
    if (num === "-") return "-";
    if (num === undefined || num === null) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return 0;
    return parsed % 1 === 0 ? parsed : parseFloat(parsed.toFixed(2));
  };

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch dropdown filter data (departments)
  const fetchFilterData = useCallback(async () => {
    try {
      const fd = new FormData();
      const response = await server_post_data(get_report_filter_data, fd);
      if (!response.data.error) {
        setDepartments(response.data.message.departments || []);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  }, []);

  // department_id -> department_name lookup, since the report endpoint only
  // returns department_id, not the name.
  const departmentNameMap = useMemo(() => {
    const map = {};
    departments.forEach((d) => {
      map[String(d.primary_id)] = d.department_name;
    });
    return map;
  }, [departments]);

  // Fetch adjustment data
  const fetchAdjustmentData = useCallback(
    async (start_date, end_date, flag = "1") => {
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay()),
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0,
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startCal || !endCal) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
          start_date = startCal;
          end_date = endCal;
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", formatDateLocal(start_date));
      fd.append("end_date", formatDateLocal(end_date));
      // Backend reads request.POST.get('difference_type'), not 'adjustment_type'
      if (differenceType !== "all")
        fd.append("difference_type", differenceType);
      if (reason.trim() !== "") fd.append("reason", reason.trim());
      if (departmentId !== "all") fd.append("department_id", departmentId);

      try {
        const response = await server_post_data(get_adjustment_report, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const summary = response.data.summary || {};

          // Gain/Loss ab ₹ me — alag unit ki qty (KG + ML) jodna galat tha.
          // Item level qty ingredient ke apne unit me hai.
          const data = (response.data.data || []).map((a) => ({
            ...a,
            adjustment_number: a.adjustment_no ?? a.adjustment_number,
            adjustment_notes: a.notes ?? a.adjustment_notes,
            total_gain_qty: a.gain_value ?? a.total_gain_qty ?? 0,
            total_loss_qty: a.loss_value ?? a.total_loss_qty ?? 0,
            net_adjustment: a.net_value ?? a.net_adjustment ?? 0,
            items: (a.items || []).map((i) => ({
              ...i,
              difference_type: i.type ?? i.difference_type,
              unit_name: i.unit || i.unit_name,
            })),
          }));

          setAdjustmentData(data);
          setExpandedAdjId(null);
          setCurrentPage(1);
          setStats({
            totalAdjustments: summary.total_adjustments || 0,
            totalGainQty:
              summary.total_gain_value ?? summary.total_gain_qty ?? 0,
            totalLossQty:
              summary.total_loss_value ?? summary.total_loss_qty ?? 0,
            netAdjustment: summary.net_value ?? summary.net_adjustment ?? 0,
          });
        }
      } catch (error) {
        console.error("Error fetching adjustment data:", error);
        toast({
          title: "Error",
          description: "Failed to load adjustment data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [
      filterOption,
      startCal,
      endCal,
      differenceType,
      reason,
      departmentId,
      toast,
    ],
  );

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    fetchAdjustmentData(startCal, endCal, "2");
  }, [startCal, endCal, fetchAdjustmentData]);

  // Excel export - 2 sheets (adjustment summary + item detail)
  function downloadExcel(orderData, filename = "Adjustment_Report.xlsx") {
    if (!orderData.length) {
      handleError("No data available for export!");
      return;
    }

    // Sheet 1: Adjustment level
    const adjSheetData = orderData.map((item, index) => ({
      "Sr. No.": index + 1,
      "Adjustment No.": item.adjustment_number || "-",
      Date: item.adjustment_date || "-",
      Department: departmentNameMap[String(item.department_id)] || "-",
      "Total Items": item.total_items,
      "Gain (₹)": formatNumber(item.total_gain_qty),
      "Loss (₹)": formatNumber(item.total_loss_qty),
      "Net (₹)": formatNumber(item.net_adjustment),
      "Adjusted By": item.adjusted_by || "-",
      Notes: item.adjustment_notes || "-",
    }));

    adjSheetData.push({
      "Sr. No.": "",
      "Adjustment No.": "",
      Date: "",
      Department: "TOTAL",
      "Total Items": "",
      "Gain (₹)": formatNumber(stats.totalGainQty),
      "Loss (₹)": formatNumber(stats.totalLossQty),
      "Net (₹)": formatNumber(stats.netAdjustment),
      "Adjusted By": `Total Adjustments: ${stats.totalAdjustments}`,
      Notes: "",
    });

    // Sheet 2: Item level
    const itemSheetData = [];
    orderData.forEach((adj) => {
      (adj.items || []).forEach((item) => {
        itemSheetData.push({
          "Adjustment No.": adj.adjustment_number || "-",
          Date: adj.adjustment_date || "-",
          Department: departmentNameMap[String(adj.department_id)] || "-",
          Ingredient: item.ingredient_name || "-",
          Type: item.difference_type || "-",
          "Adjustment Qty": formatNumber(item.adjustment_qty),
          Unit: item.unit_name || "-",
          Reason: item.reason || "-",
        });
      });
    });

    const adjSheet = XLSX.utils.json_to_sheet(adjSheetData);
    adjSheet["!cols"] = [
      { wch: 8 },
      { wch: 16 },
      { wch: 12 },
      { wch: 18 },
      { wch: 11 },
      { wch: 11 },
      { wch: 11 },
      { wch: 14 },
      { wch: 18 },
      { wch: 25 },
    ];

    const itemSheet = XLSX.utils.json_to_sheet(itemSheetData);
    itemSheet["!cols"] = [
      { wch: 16 },
      { wch: 12 },
      { wch: 18 },
      { wch: 22 },
      { wch: 8 },
      { wch: 14 },
      { wch: 10 },
      { wch: 25 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, adjSheet, "Adjustment Summary");
    XLSX.utils.book_append_sheet(workbook, itemSheet, "Item Detail");
    XLSX.writeFile(workbook, filename);
  }

  // Pagination
  const totalPages = Math.ceil(adjustmentData.length / entriesPerPage);
  const currentData = adjustmentData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    setExpandedAdjId(null);
  };

  const toggleExpand = (adjId) => {
    setExpandedAdjId(expandedAdjId === adjId ? null : adjId);
  };

  // Format date for table display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString.replace(" ", "T")).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const netAdjustmentClass = (value) => {
    if (value > 0) return "text-success";
    if (value < 0) return "text-destructive";
    return "text-foreground";
  };

  // Initial load
  useEffect(() => {
    fetchFilterData();
    fetchAdjustmentData(startCal, endCal, "1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainLayout>
      <Header
        title="Adjustment Report"
        subtitle="Stock adjustments with gain/loss reasons"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <SlidersHorizontal className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalAdjustments}
                </p>
                <p className="text-sm text-muted-foreground">
                  Total Adjustments
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-success">
                  ₹{formatNumber(stats.totalGainQty)}
                </p>
                <p className="text-sm text-muted-foreground">Gain Value</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-destructive">
                  ₹{formatNumber(stats.totalLossQty)}
                </p>
                <p className="text-sm text-muted-foreground">Loss Value</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <ArrowLeftRight className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p
                  className={cn(
                    "text-2xl font-bold",
                    netAdjustmentClass(stats.netAdjustment),
                  )}
                >
                  {stats.netAdjustment > 0 ? "+" : ""}₹
                  {formatNumber(stats.netAdjustment)}
                </p>
                <p className="text-sm text-muted-foreground">Net Adjustment</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Date Filter Dropdown */}
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Difference Type */}
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={differenceType}
                  onValueChange={setDifferenceType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {ADJUSTMENT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Department */}
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={departmentId} onValueChange={setDepartmentId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem
                        key={dept.primary_id}
                        value={String(dept.primary_id)}
                      >
                        {dept.department_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Reason (matches items with reason__icontains) */}
              <div className="space-y-2">
                <Label>Reason</Label>
                <Input
                  type="text"
                  placeholder="Search by reason..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadExcel(adjustmentData)}
                  className="gap-2"
                  disabled={adjustmentData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground w-10"></TableHead>
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">
                    Adjustment No.
                  </TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">
                    Department
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Items
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Gain (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Loss (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Net (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Adjusted By
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="text-center text-muted-foreground"
                    >
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((row, index) => (
                    <Fragment key={row.adjustment_id || index}>
                      <TableRow
                        className="border-border cursor-pointer"
                        onClick={() => toggleExpand(row.adjustment_id)}
                      >
                        <TableCell>
                          {expandedAdjId === row.adjustment_id ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>
                          {(currentPage - 1) * entriesPerPage + index + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          {row.adjustment_number || "-"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(row.adjustment_date)}
                        </TableCell>
                        <TableCell>
                          {departmentNameMap[String(row.department_id)] || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.total_items}
                        </TableCell>
                        <TableCell className="text-right text-success font-medium">
                          {formatNumber(row.total_gain_qty)}
                        </TableCell>
                        <TableCell className="text-right text-destructive font-medium">
                          {formatNumber(row.total_loss_qty)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right font-semibold",
                            netAdjustmentClass(row.net_adjustment),
                          )}
                        >
                          {row.net_adjustment > 0 ? "+" : ""}
                          {formatNumber(row.net_adjustment)}
                        </TableCell>
                        <TableCell>{row.adjusted_by || "-"}</TableCell>
                      </TableRow>

                      {/* Expanded item detail */}
                      {expandedAdjId === row.adjustment_id && (
                        <TableRow className="border-border bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={10} className="p-4">
                            {row.adjustment_notes && (
                              <div className="mb-3 text-xs text-muted-foreground">
                                Notes: {row.adjustment_notes}
                              </div>
                            )}
                            <div className="rounded-lg border border-border bg-card overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-border hover:bg-transparent">
                                    <TableHead className="text-muted-foreground text-xs">
                                      Ingredient
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs">
                                      Type
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Qty
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs">
                                      Unit
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs">
                                      Reason
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(row.items || []).map((item, i) => (
                                    <TableRow
                                      key={`${row.adjustment_id}-${item.ingredient_id}-${i}`}
                                      className="border-border"
                                    >
                                      <TableCell className="font-medium text-sm">
                                        {item.ingredient_name || "-"}
                                      </TableCell>
                                      <TableCell>
                                        <span
                                          className={cn(
                                            "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium",
                                            typeBadgeClass(
                                              item.difference_type,
                                            ),
                                          )}
                                        >
                                          {item.difference_type || "N/A"}
                                        </span>
                                      </TableCell>
                                      <TableCell
                                        className={cn(
                                          "text-right text-sm font-medium",
                                          item.difference_type === "Gain"
                                            ? "text-success"
                                            : "text-destructive",
                                        )}
                                      >
                                        {item.difference_type === "Gain"
                                          ? "+"
                                          : "-"}
                                        {formatNumber(item.adjustment_qty)}
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {item.unit_name || "-"}
                                      </TableCell>
                                      <TableCell
                                        className="text-sm max-w-[220px] truncate"
                                        title={item.reason || ""}
                                      >
                                        {item.reason || "-"}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, adjustmentData.length)}{" "}
                of {adjustmentData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
